/**
 * Feature flags for toggling features on/off
 *
 * This file controls global feature availability.
 * Update these flags to enable/disable features across the application.
 */

export const FEATURE_FLAGS = {
  /**
   * Show subscription/upgrade UI and payment features
   *
   * When false:
   * - Hides upgrade prompts and dialogs
   * - Hides trial banners
   * - Hides pricing/plan cards
   * - Disables all payment flows
   *
   * Set to false during soft launch when not accepting payments yet.
   */
  ENABLE_SUBSCRIPTIONS: true,

  /**
   * Show usage metrics and limit warnings
   *
   * When false:
   * - Hides usage limit warnings
   * - Hides usage metrics displays
   */
  SHOW_USAGE_METRICS: false,

  /**
   * Show custom field management UI
   *
   * When false:
   * - Hides "Custom Fields" entry in the settings sidebar
   * - Redirects /settings/custom-fields and /settings/custom-fields/tabs to /settings
   *
   * Kept off until the feature is fully tested before public release.
   */
  ENABLE_CUSTOM_FIELDS: false,

  /**
   * Show work item type management + selection UI.
   *
   * When false:
   * - Hides the "Task Types" entry in the settings sidebar and redirects
   *   /settings/work-item-types to /settings
   * - Hides the per-project "enable work item types" toggle
   * - Hides all work item type pickers on task forms (quick create, add-task
   *   bar, task detail, board) and type badges in task lists
   * - Hides the work-item-type scoping section in the custom fields editor
   *
   * The product model is task > subtask only. Backend/DB/sync keep the field;
   * this only hides the UI, so flipping back to true restores every surface.
   */
  ENABLE_WORK_ITEM_TYPES: false,

  /**
   * Show Moly Super Agent UI.
   *
   * When false:
   * - Hides the Super Agent bot-token section in workspace settings
   * - Hides the Super Agent section in project settings
   * - Hides the AI gate field on task details
   * - Removes the agent from every assignee picker (existing assignments are kept)
   * - Hides the agent badge on board cards and in the members list
   *
   * UI-only: the backend endpoints (bot token, /queue, /active) stay live, so
   * flipping back to true restores every surface.
   */
  ENABLE_SUPER_AGENT: false,

  /**
   * Show the GitHub integration.
   *
   * When false:
   * - Hides the workspace GitHub connection (Integrations)
   * - Hides the per-project repository link in project settings
   * - Hides linked pull requests on task details
   * - Hides the PR / CI indicator in task lists and on board cards
   *
   * UI-only: the backend integration, its webhooks and PR sync stay live, and an
   * existing connection is preserved — flipping this back to true restores every
   * surface with its configuration intact.
   */
  ENABLE_GITHUB_INTEGRATION: false,
} as const

export type FeatureFlag = keyof typeof FEATURE_FLAGS
